import { useState } from "react";
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import './VerifySetup.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

// Simple icons as SVG components
const CheckCircle2 = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const XCircle = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const Loader2 = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

// Simple confetti function
const confetti = (options) => {
  console.log('Confetti would show here!', options);
};

const Index = () => {
  const { getToken } = useAuth();
  const [salesPageUrl, setSalesPageUrl] = useState("");
  const [thankYouPageUrl, setThankYouPageUrl] = useState("");
  const [isCallFunnel, setIsCallFunnel] = useState(false);
  const [callBookingThankYouPageUrl, setCallBookingThankYouPageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  const toast = (options) => {
    console.log(options.title, options.description);
  };

  const handleVerify = async () => {
    if (!salesPageUrl || !thankYouPageUrl) {
      toast({
        title: "Missing URLs",
        description: "Please enter both sales page and thank you page URLs",
        variant: "destructive",
      });
      return;
    }

    if (isCallFunnel && !callBookingThankYouPageUrl) {
      toast({
        title: "Missing Call Booking Page URL",
        description: "Please enter the call booking thank you page URL",
        variant: "destructive",
      });
      return;
    }

    // Normalize and validate URLs to avoid 400 errors from the backend
    const ensureProtocol = (u) => {
      const t = u.trim();
      if (!t) return t;
      return /^https?:\/\//i.test(t) ? t : `https://${t}`;
    };
    const isValidUrl = (u) => {
      try {
        // new URL requires protocol; we ensure it above
        // This will throw if the URL is still invalid
        new URL(u);
        return true;
      } catch (_) {
        return false;
      }
    };

    const normalizedSales = ensureProtocol(salesPageUrl);
    const normalizedThank = ensureProtocol(thankYouPageUrl);
    const normalizedCall = isCallFunnel && callBookingThankYouPageUrl
      ? ensureProtocol(callBookingThankYouPageUrl)
      : undefined;

    if (!isValidUrl(normalizedSales) || !isValidUrl(normalizedThank) || (isCallFunnel && normalizedCall && !isValidUrl(normalizedCall))) {
      toast({
        title: "Invalid URL format",
        description: "Please use full URLs, e.g. https://example.com/path",
        variant: "destructive",
      });
      return;
    }

    // Update inputs to normalized forms so users see the corrected scheme
    if (normalizedSales !== salesPageUrl) setSalesPageUrl(normalizedSales);
    if (normalizedThank !== thankYouPageUrl) setThankYouPageUrl(normalizedThank);
    if (isCallFunnel && normalizedCall && normalizedCall !== callBookingThankYouPageUrl) {
      setCallBookingThankYouPageUrl(normalizedCall);
    }

    setIsLoading(true);
    setResult(null);

    try {
      const token = await getToken();
      const response = await axios.post(`https://${SERVER_URL}/api/verify-tracking-codes`, {
        salesPageUrl: normalizedSales,
        thankYouPageUrl: normalizedThank,
        isCallFunnel,
        callBookingThankYouPageUrl: isCallFunnel ? normalizedCall : undefined,
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = response.data;

      if (!data) {
        throw new Error('No data received from verification function');
      }

      console.log('Verification data received:', data);
      setResult(data);

      const allValid = data.salesPage.valid && 
        data.thankYouPage.valid && 
        (!data.callBookingThankYouPage || data.callBookingThankYouPage.valid) &&
        data.offerContentMatch && 
        data.sameDomain;
      
      // Trigger confetti celebration if everything is valid
      if (allValid) {
        const duration = 3000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

        const randomInRange = (min, max) => {
          return Math.random() * (max - min) + min;
        };

        const interval = setInterval(() => {
          const timeLeft = animationEnd - Date.now();

          if (timeLeft <= 0) {
            return clearInterval(interval);
          }

          const particleCount = 50 * (timeLeft / duration);
          
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
          });
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
          });
        }, 250);
      }
      
      toast({
        title: allValid ? "All Codes Verified!" : "Issues Detected",
        description: allValid 
          ? "Both pages have all required tracking codes configured correctly." 
          : allValid === false && !data.salesPage.error && !data.thankYouPage.error
            ? "Issues found. If you believe this is incorrect, please double-check that your URLs are complete and haven't had any characters deleted."
            : "Issues detected with your tracking setup. Check the results below.",
        variant: allValid ? "default" : "destructive",
      });
    } catch (error) {
      console.error("Verification error:", error);
      toast({
        title: "Verification Failed",
        description: error instanceof Error ? error.message : "Failed to verify tracking codes",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const CheckItem = ({ label, passed }) => (
    <div className="flex items-center gap-2 py-2">
      {passed ? (
        <CheckCircle2 className="h-5 w-5 text-green-600" />
      ) : (
        <XCircle className="h-5 w-5 text-destructive" />
      )}
      <span className={passed ? "text-green-700" : "text-destructive"}>{label}</span>
    </div>
  );

  return (
    <div className="setup-guide-container">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h4 className="heading-h4">
            RevTrack Set Up Check
          </h4>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Verify that your sales and thank you pages have the correct RevTrack codes installed
          </p>
        </div>

        {/* Input Form */}
        <div className="verify-setup-card">
          <div className="verify-setup-card-header">
            <div className="verify-setup-title-section">
              <h2 className="verify-setup-card-title">Enter Your Page URLs</h2>
              <p className="verify-setup-card-description">
                Paste the URLs of your sales page and thank you page to verify tracking codes
              </p>
            </div>
            {/* Call Funnel Toggle - Top Right */}
            <div className="call-funnel-toggle">
              <label htmlFor="call-funnel-toggle" className="toggle-label">
                Sales Call Funnel
              </label>
              <input
                id="call-funnel-toggle"
                type="checkbox"
                checked={isCallFunnel}
                onChange={(e) => setIsCallFunnel(e.target.checked)}
                disabled={isLoading}
                className="toggle-switch"
              />
            </div>
          </div>
          <div className="verify-setup-card-content">
            <div className="input-group">
              <label htmlFor="sales-url">Sales Page URL</label>
              <input
                id="sales-url"
                type="url"
                placeholder="https://example.com/sales"
                value={salesPageUrl}
                onChange={(e) => setSalesPageUrl(e.target.value)}
                disabled={isLoading}
                className="url-input"
              />
            </div>
            {/* Call Booking Thank You Page URL Input */}
            {isCallFunnel && (
              <div className="input-group">
                <label htmlFor="call-booking-url">Call Booking Thank You Page URL</label>
                <input
                  id="call-booking-url"
                  type="url"
                  placeholder="https://example.com/call-booked"
                  value={callBookingThankYouPageUrl}
                  onChange={(e) => setCallBookingThankYouPageUrl(e.target.value)}
                  disabled={isLoading}
                  className="url-input"
                />
                <p className="input-description">
                  Page shown immediately after someone books a call
                </p>
              </div>
            )}
            <div className="input-group">
              <label htmlFor="thankyou-url">
                {isCallFunnel ? "Final Thank You Page URL" : "Thank You Page URL"}
              </label>
              <input
                id="thankyou-url"
                type="url"
                placeholder="https://example.com/thank-you"
                value={thankYouPageUrl}
                onChange={(e) => setThankYouPageUrl(e.target.value)}
                disabled={isLoading}
                className="url-input"
              />
              {isCallFunnel && (
                <p className="input-description">
                  Page shown after someone purchases
                </p>
              )}
            </div>
            <button 
              onClick={handleVerify} 
              disabled={isLoading} 
              className="verify-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="button-icon animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify Tracking Codes"
              )}
            </button>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="space-y-6">
            {/* Show note if any validation fails */}
            {(!result.salesPage.valid || !result.thankYouPage.valid || 
              (result.callBookingThankYouPage && !result.callBookingThankYouPage.valid) ||
              !result.offerContentMatch || !result.sameDomain) && (
              <div className="verification-note">
                <p>
                  <strong>Note:</strong> The verification tool is sometimes wrong. Just because this test fails does not mean the set up is necessarily wrong. To test it, click a tracking link & go through the funnel flow. If data appears in Content, you can ignore these errors.
                </p>
              </div>
            )}

            {/* Domain Match Check */}
            <div className="verify-setup-card">
              <div className="verify-setup-card-header">
                <div className="verify-setup-card-title-section">
                  <h3 className="verify-setup-card-title">Domain Match</h3>
                  <div className="verify-setup-card-header-icon">
                  {result.sameDomain ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-destructive" />
                  )}
                  </div>
                </div>
                <p className="verify-setup-card-description">
                  Sales and Thank You pages must be on the same domain
                </p>
              </div>
              <div className="verify-setup-card-content">
                {!result.sameDomain && result.domainError && (
                  <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                    ⚠️ {result.domainError}
                  </div>
                )}
                {result.sameDomain && (
                  <div className="text-green-700">
                    ✓ Both pages are on the same domain
                  </div>
                )}
              </div>
            </div>

            {/* Offer Content Match Check */}
            <div className="verify-setup-card">
              <div className="verify-setup-card-header">
                <div className="verify-setup-card-title-section">
                  <h3 className="verify-setup-card-title">Offer Name Match</h3>
                  <div className="verify-setup-card-header-icon">
                  {result.offerContentMatch ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-destructive" />
                  )}
                  </div>
                </div>
                <p className="verify-setup-card-description">
                  Both pages must have the same offer name in their meta tags
                </p>
              </div>
              <div className="verify-setup-card-content">
                <div className="space-y-2">
                  <div>
                    <span className="font-medium">Sales Page Offer: </span>
                    <span className="text-muted-foreground">
                      {result.salesPage.offerContent || "Not found"}
                    </span>
                  </div>
                  {result.callBookingThankYouPage && (
                    <div>
                      <span className="font-medium">Call Booking Page Offer: </span>
                      <span className="text-muted-foreground">
                        {result.callBookingThankYouPage.offerContent || "Not found"}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Thank You Page Offer: </span>
                    <span className="text-muted-foreground">
                      {result.thankYouPage.offerContent || "Not found"}
                    </span>
                  </div>
                  {!result.offerContentMatch && (
                    <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                      ⚠️ Offer names don't match! All pages must have the same offer name.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sales Page Results */}
            <div className="verify-setup-card">
              <div className="verify-setup-card-header">
                <div className="verify-setup-card-title-section">
                  <h3 className="verify-setup-card-title">Sales Page</h3>
                  <div className="verify-setup-card-header-icon">
                  {result.salesPage.valid ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-destructive" />
                  )}
                  </div>
                </div>
                <p className="verify-setup-card-description break-all">{result.salesPage.url}</p>
              </div>
              <div className="verify-setup-card-content">
                {result.salesPage.error ? (
                  <div className="text-destructive">Error: {result.salesPage.error}</div>
                ) : (
                  <div className="space-y-1">
                    <CheckItem
                      label="Username meta tag"
                      passed={result.salesPage.checks.usernameMeta ?? false}
                    />
                    {!result.salesPage.checks.usernameMeta && (
                      <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm space-y-2">
                        <p className="font-semibold">⚠️ Problem: Username meta tag is missing!</p>
                        <p>Your sales page needs a username meta tag to identify you.</p>
                      </div>
                    )}
                    <CheckItem
                      label="Offer meta tag"
                      passed={result.salesPage.checks.offerMeta}
                    />
                    {!result.salesPage.checks.offerMeta && (
                      <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm space-y-2">
                        <p className="font-semibold">⚠️ Problem: Offer meta tag is missing!</p>
                        <p>Your sales page needs an offer meta tag to identify which offer this is.</p>
                      </div>
                    )}
                    <CheckItem
                      label="Sales Page Code"
                      passed={result.salesPage.checks.cloudFrontScript}
                    />
                    {!result.salesPage.checks.cloudFrontScript && (
                      <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm space-y-2">
                        <p className="font-semibold">⚠️ Problem: Sales Page Code is missing!</p>
                        <p>Your sales page needs the Sales Page tracking code to work properly.</p>
                      </div>
                    )}
                    <CheckItem
                      label="No duplicate scripts"
                      passed={result.salesPage.checks.noDuplicateScripts ?? true}
                    />
                    {result.salesPage.checks.noDuplicateScripts === false && (
                      <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm space-y-3">
                        <p className="font-semibold">⚠️ Problem: Multiple tracking codes found on your sales page</p>
                        <div>
                          <p className="mb-2">Here's what we detected:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            {result.salesPage.scriptCounts?.tracker > 0 && (
                              <li>Sales Page Code: {result.salesPage.scriptCounts.tracker} {result.salesPage.scriptCounts.tracker === 1 ? 'copy' : 'copies'} ✓ (correct)</li>
                            )}
                            {result.salesPage.scriptCounts?.thankYou > 0 && (
                              <li className="text-red-600 font-medium">Thank You Page Code: {result.salesPage.scriptCounts.thankYou} {result.salesPage.scriptCounts.thankYou === 1 ? 'copy' : 'copies'} ✗ (shouldn't be here)</li>
                            )}
                            {result.salesPage.scriptCounts?.callBooking > 0 && (
                              <li className="text-red-600 font-medium">Call Booking Page Code: {result.salesPage.scriptCounts.callBooking} {result.salesPage.scriptCounts.callBooking === 1 ? 'copy' : 'copies'} ✗ (shouldn't be here)</li>
                            )}
                          </ul>
                        </div>
                        <div>
                          <p className="font-semibold mb-1">How to fix:</p>
                          <p>Remove {result.salesPage.scriptCounts?.thankYou > 0 ? 'the Thank You Page Code' : ''}{result.salesPage.scriptCounts?.thankYou > 0 && result.salesPage.scriptCounts?.callBooking > 0 ? ' and ' : ''}{result.salesPage.scriptCounts?.callBooking > 0 ? 'the Call Booking Page Code' : ''} from your sales page. Only the Sales Page Code should remain.</p>
                        </div>
                      </div>
                    )}
                    {result.salesPage.redirected && (
                      <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                        ⚠️ URL redirected to: {result.salesPage.finalUrl}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Call Booking Thank You Page Results */}
            {result.callBookingThankYouPage && (
              <div className="verify-setup-card">
                <div className="verify-setup-card-header">
                  <div className="verify-setup-card-title-section">
                    <h3 className="verify-setup-card-title">Call Booking Thank You Page</h3>
                    <div className="verify-setup-card-header-icon">
                    {result.callBookingThankYouPage.valid ? (
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    ) : (
                      <XCircle className="h-6 w-6 text-destructive" />
                    )}
                    </div>
                  </div>
                  <p className="verify-setup-card-description break-all">{result.callBookingThankYouPage.url}</p>
                </div>
                <div className="verify-setup-card-content">
                  {result.callBookingThankYouPage.error ? (
                    <div className="text-destructive">Error: {result.callBookingThankYouPage.error}</div>
                  ) : (
                    <div className="space-y-1">
                      <CheckItem
                        label="Offer meta tag"
                        passed={result.callBookingThankYouPage.checks.offerMeta}
                      />
                      {!result.callBookingThankYouPage.checks.offerMeta && (
                        <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm space-y-2">
                          <p className="font-semibold">⚠️ Problem: Offer meta tag is missing!</p>
                          <p>Your call booking page needs an offer meta tag (same as your sales page).</p>
                        </div>
                      )}
                      <CheckItem
                        label="Call Booking Page Code"
                        passed={result.callBookingThankYouPage.checks.cloudFrontScript}
                      />
                      {!result.callBookingThankYouPage.checks.cloudFrontScript && (
                        <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm space-y-2">
                          <p className="font-semibold">⚠️ Problem: Call Booking Page Code is missing!</p>
                          <p>Your call booking thank you page needs the Call Booking tracking code.</p>
                        </div>
                      )}
                      <CheckItem
                        label="No duplicate scripts"
                        passed={result.callBookingThankYouPage.checks.noDuplicateScripts ?? true}
                      />
                      {result.callBookingThankYouPage.checks.noDuplicateScripts === false && (
                        <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm space-y-3">
                          <p className="font-semibold">⚠️ Problem: Multiple tracking codes found on your call booking page</p>
                          <div>
                            <p className="mb-2">Here's what we detected:</p>
                            <ul className="list-disc list-inside ml-2 space-y-1">
                              {result.callBookingThankYouPage.scriptCounts?.tracker > 0 && (
                                <li className="text-red-600 font-medium">Sales Page Code: {result.callBookingThankYouPage.scriptCounts.tracker} {result.callBookingThankYouPage.scriptCounts.tracker === 1 ? 'copy' : 'copies'} ✗ (shouldn't be here)</li>
                              )}
                              {result.callBookingThankYouPage.scriptCounts?.thankYou > 0 && (
                                <li className="text-red-600 font-medium">Thank You Page Code: {result.callBookingThankYouPage.scriptCounts.thankYou} {result.callBookingThankYouPage.scriptCounts.thankYou === 1 ? 'copy' : 'copies'} ✗ (shouldn't be here)</li>
                              )}
                              {result.callBookingThankYouPage.scriptCounts?.callBooking > 0 && (
                                <li>Call Booking Page Code: {result.callBookingThankYouPage.scriptCounts.callBooking} {result.callBookingThankYouPage.scriptCounts.callBooking === 1 ? 'copy' : 'copies'} ✓ (correct)</li>
                              )}
                            </ul>
                          </div>
                          <div>
                            <p className="font-semibold mb-1">How to fix:</p>
                            <p>Remove {result.callBookingThankYouPage.scriptCounts?.tracker > 0 ? 'the Sales Page Code' : ''}{result.callBookingThankYouPage.scriptCounts?.tracker > 0 && result.callBookingThankYouPage.scriptCounts?.thankYou > 0 ? ' and ' : ''}{result.callBookingThankYouPage.scriptCounts?.thankYou > 0 ? 'the Thank You Page Code' : ''} from your call booking page. Only the Call Booking Page Code should remain.</p>
                          </div>
                        </div>
                      )}
                      {result.callBookingThankYouPage.redirected && (
                        <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                          ⚠️ URL redirected to: {result.callBookingThankYouPage.finalUrl}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Thank You Page Results */}
            <div className="verify-setup-card">
              <div className="verify-setup-card-header">
                <div className="verify-setup-card-title-section">
                  <h3 className="verify-setup-card-title">Thank You Page</h3>
                  <div className="verify-setup-card-header-icon">
                  {result.thankYouPage.valid ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-destructive" />
                  )}
                  </div>
                </div>
                <p className="verify-setup-card-description break-all">{result.thankYouPage.url}</p>
              </div>
              <div className="verify-setup-card-content">
                {result.thankYouPage.error ? (
                  <div className="text-destructive">Error: {result.thankYouPage.error}</div>
                ) : (
                  <div className="space-y-1">
                    <CheckItem
                      label="Offer meta tag"
                      passed={result.thankYouPage.checks.offerMeta}
                    />
                    {!result.thankYouPage.checks.offerMeta && (
                      <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm space-y-2">
                        <p className="font-semibold">⚠️ Problem: Offer meta tag is missing!</p>
                        <p>Your thank you page needs an offer meta tag (same as your sales page).</p>
                      </div>
                    )}
                    <CheckItem
                      label="Thank You Page Code"
                      passed={result.thankYouPage.checks.cloudFrontScript}
                    />
                    {!result.thankYouPage.checks.cloudFrontScript && (
                      <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm space-y-2">
                        <p className="font-semibold">⚠️ Problem: Thank You Page Code is missing!</p>
                        <p>Your thank you page needs the Thank You tracking code to work properly.</p>
                      </div>
                    )}
                    <CheckItem
                      label="No duplicate scripts"
                      passed={result.thankYouPage.checks.noDuplicateScripts ?? true}
                    />
                    {result.thankYouPage.checks.noDuplicateScripts === false && (
                      <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm space-y-3">
                        <p className="font-semibold">⚠️ Problem: {result.thankYouPage.scriptCounts?.thankYou > 1 ? 'Multiple copies of the same code' : 'Wrong tracking codes'} found on your thank you page</p>
                        <div>
                          <p className="mb-2">Here's what we detected:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            {result.thankYouPage.scriptCounts?.tracker > 0 && (
                              <li className="text-red-600 font-medium">Sales Page Code: {result.thankYouPage.scriptCounts.tracker} {result.thankYouPage.scriptCounts.tracker === 1 ? 'copy' : 'copies'} ✗ (shouldn't be here)</li>
                            )}
                            {result.thankYouPage.scriptCounts?.callBooking > 0 && (
                              <li className="text-red-600 font-medium">Call Booking Page Code: {result.thankYouPage.scriptCounts.callBooking} {result.thankYouPage.scriptCounts.callBooking === 1 ? 'copy' : 'copies'} ✗ (shouldn't be here)</li>
                            )}
                            {result.thankYouPage.scriptCounts?.thankYou > 0 && (
                              <li className={result.thankYouPage.scriptCounts.thankYou === 1 ? '' : 'text-red-600 font-medium'}>Thank You Page Code: {result.thankYouPage.scriptCounts.thankYou} {result.thankYouPage.scriptCounts.thankYou === 1 ? 'copy' : 'copies'} {result.thankYouPage.scriptCounts.thankYou === 1 ? '✓ (correct)' : '✗ (too many copies)'}</li>
                            )}
                          </ul>
                        </div>
                        <div>
                          <p className="font-semibold mb-1">How to fix:</p>
                          <p>{result.thankYouPage.scriptCounts?.thankYou > 1 ? `Delete ${result.thankYouPage.scriptCounts.thankYou - 1} extra ${result.thankYouPage.scriptCounts.thankYou - 1 === 1 ? 'copy' : 'copies'} of the Thank You Page Code` : 'Remove the wrong codes'}{result.thankYouPage.scriptCounts?.tracker > 0 || result.thankYouPage.scriptCounts?.callBooking > 0 ? ', and also remove ' : ''}{result.thankYouPage.scriptCounts?.tracker > 0 ? 'the Sales Page Code' : ''}{result.thankYouPage.scriptCounts?.tracker > 0 && result.thankYouPage.scriptCounts?.callBooking > 0 ? ' and ' : ''}{result.thankYouPage.scriptCounts?.callBooking > 0 ? 'the Call Booking Page Code' : ''} from your thank you page. You should have exactly ONE Thank You Page Code and nothing else.</p>
                        </div>
                      </div>
                    )}
                    {result.thankYouPage.redirected && (
                      <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                        ⚠️ URL redirected to: {result.thankYouPage.finalUrl}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <button 
              onClick={() => {
                setSalesPageUrl("");
                setThankYouPageUrl("");
                setCallBookingThankYouPageUrl("");
                setIsCallFunnel(false);
                setResult(null);
              }}
              className="verify-setup-reset-button"
            >
              Check Another
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Index;
